/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import {
  FOOTER_LINKS,
  EXPLORE_MENU_LINKS,
  getNamedLinkRouteProps,
} from '../../../../util/footerNavigation';

import {
  AvatarMedium,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { getUserTypePreference } from '../../../../util/dataExtractor';
import { USER_TYPE } from '../../../../util/enums';
import useCommunityMenuOptions from '../../../../hooks/useCommunityMenuOptions';
import { canManageShopNewBrands } from '../../../../util/shopNewBrands';
import { travelMenuOptions } from '../../../../config/configExtras';

const getCurrentPageClassForRoute = (currentPage, route = {}) => {
  const page = route?.name;
  const hasPageName = name => currentPage?.indexOf(name) === 0;
  const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
  const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
  const isCurrentPage = currentPage === page;

  return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
    ? css.currentPage
    : null;
};

const NavigationSection = ({ title, links, currentPage }) => {
  const isExploreSection = title.toLowerCase() === 'explore';

  return (
    <section className={css.navigationSection} aria-label={title}>
      <h3 className={css.navigationSectionTitle}>{title}</h3>
      <div
        className={classNames(css.sectionLinksWrapper, {
          [css.exploreLinksWrapper]: isExploreSection,
        })}
      >
        {links.map((link, index) => {
          const namedLinkProps = getNamedLinkRouteProps(link);
          const currentPageClass = getCurrentPageClassForRoute(currentPage, link.route);

          return namedLinkProps ? (
            <NamedLink
              key={`${link.label}-${index}`}
              className={classNames(css.navigationLink, currentPageClass)}
              {...namedLinkProps}
            >
              <span className={css.menuItemBorder} />
              {link.label}
            </NamedLink>
          ) : (
            <ExternalLink
              key={`${link.label}-${index}`}
              href={link.href}
              className={classNames(css.navigationLink, currentPageClass)}
            >
              <span className={css.menuItemBorder} />
              {link.label}
            </ExternalLink>
          );
        })}
      </div>
    </section>
  );
};

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { text, type, href, route } = linkConfig;

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(
      css.navigationLink,
      getCurrentPageClassForRoute(currentPage, route)
    );
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
  } = props;
  const communityMenuOptions = useCommunityMenuOptions();

  const user = ensureCurrentUser(currentUser);
  const isOurAffiliatesPage = currentPage === 'OurAffiliatesPage';

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;
  const companyLinksForMobileMenu = FOOTER_LINKS.company.filter(
    link => !['OurAffiliatesPage', 'RetreatsPage'].includes(link?.route?.name)
  );
  const footerSections = (
    <div className={css.footerLinksSections}>
      <NavigationSection title="Travel" links={travelMenuOptions} currentPage={currentPage} />
      {EXPLORE_MENU_LINKS.shop.map(group => (
        <NavigationSection
          key={group.title}
          title={group.title}
          links={group.links}
          currentPage={currentPage}
        />
      ))}
      <div className={css.communityLinksWrapper}>
        {communityMenuOptions.map(link => {
          const namedLinkProps = getNamedLinkRouteProps(link);
          const currentPageClass = getCurrentPageClassForRoute(currentPage, link.route);

          return namedLinkProps ? (
            <NamedLink
              key={link.id}
              className={classNames(
                css.navigationLink,
                css.communityNavigationLink,
                currentPageClass
              )}
              {...namedLinkProps}
            >
              {link.label}
            </NamedLink>
          ) : (
            <ExternalLink
              key={link.id}
              href={link.href}
              className={classNames(
                css.navigationLink,
                css.communityNavigationLink,
                currentPageClass
              )}
            >
              {link.label}
            </ExternalLink>
          );
        })}
      </div>
      <NavigationSection
        title="Company"
        links={companyLinksForMobileMenu}
        currentPage={currentPage}
      />
      <NavigationSection
        title="Explore"
        links={[...EXPLORE_MENU_LINKS.sell, ...EXPLORE_MENU_LINKS.justIn]}
        currentPage={currentPage}
      />
    </div>
  );

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>
          <NamedLink
            className={classNames(css.navigationLink, { [css.currentPage]: isOurAffiliatesPage })}
            name="OurAffiliatesPage"
          >
            <FormattedMessage id="OurAffiliatesPage.title" />
          </NamedLink>
          {footerSections}
          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const manageListingsLink = (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  );
  const userType = getUserTypePreference(currentUser);
  const isSeller = userType === USER_TYPE.SELLER;
  const shopNewBrandsLinkMaybe = canManageShopNewBrands(currentUser) ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ShopNewBrandsPage'))}
      name="ShopNewBrandsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.shopNewBrandsLink" />
    </NamedLink>
  ) : null;

  return (
    <div className={css.root}>
      <div className={css.mobileMenuHead}>
        <AvatarMedium className={css.avatar} user={currentUser} />
        <div className={css.mobileUser}>
          <h2 className={css.greeting}>
            <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
          </h2>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>
        </div>{' '}
      </div>
      <div className={css.content}>
        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`FavoritesPage`))}
            name="FavoritesPage"
          >
            <FormattedMessage id="TopbarDesktop.favoritePageLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('OurAffiliatesPage'))}
            name="OurAffiliatesPage"
          >
            <FormattedMessage id="OurAffiliatesPage.title" />
          </NamedLink>
          {manageListingsLink}
          {shopNewBrandsLinkMaybe}
          {isSeller ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ShopSettingsPage'))}
              name="ShopSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.shopSettingsLink" />
            </NamedLink>
          ) : (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        {footerSections}
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
